import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react'
import { CircleX } from 'lucide-react'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { emit } from '../../platform/analytics/analytics'
import { REQUIRES_RECENT_LOGIN, authErrorFor } from '../../auth/errors'
import { AuthFailure, MfaRequiredError, type MfaChallenge, type MfaFactor } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import { labelFor } from '../../auth/factorLabel'
import '../../platform/primitives/dialog.css'
import './enrol.css'

type Props = {
  open: boolean
  /** The action enum for analytics — never the title or body, which name things. */
  mode: string
  /** Also the dialog's accessible name. */
  title: string
  /** What is about to happen and, for a removal, what is lost. */
  body: ReactNode
  /** The control that proceeds once the user is re-authenticated. */
  confirmLabel: string
  pendingLabel?: string
  destructive?: boolean
  onCancel: () => void
  /**
   * The gated work. Runs ONLY after re-authentication has resolved; a
   * rejection is shown inside the dialog, which stays open. The caller closes
   * it on success.
   */
  onReauthed: () => Promise<void>
  restoreFocusTo?: RefObject<HTMLElement | null>
}

type Step = { kind: 'credential' } | { kind: 'code'; challenge: MfaChallenge }

function messageFor(thrown: unknown): string {
  return (thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')).message
}

/**
 * A fresh re-authentication in front of a destructive or sensitive action —
 * PRD-M13-R5, #188.
 *
 * **Built from `Overlay`, `Field` and `Button`; the design carries no
 * re-authentication dialog**, so this follows `ConfirmDialog`'s structure
 * (title, body, a `role="alert"` failure line, Cancel first and the confirming
 * control trailing) rather than inventing a new one. It is not a
 * `ConfirmDialog` because that component's confirm takes no input.
 *
 * **Every time, regardless of session age.** Firebase's own recent-login
 * window is its policy, not ours: nothing here asks how old the session is.
 *
 * **The gated work is `onReauthed`, and it is only called after
 * `auth.reauthenticate` has resolved** — the property the tests pin through the
 * port's call log. A failed attempt leaves the dialog open and usable.
 *
 * **An account with a second factor is asked for that code too.** Firebase
 * refuses to re-authenticate such a user on the first factor alone, and this is
 * exactly the account that reaches here. That arrives as a `MfaRequiredError`
 * whose challenge completes THIS re-authentication.
 *
 * **The password lives only in the form's state, and the form exists only while
 * the dialog is open** — closing unmounts it, and a finished attempt clears it
 * either way. Nothing here logs it or sends it to analytics, which carries the
 * outcome enum alone.
 */
export function ReauthDialog({ open, mode, onCancel, restoreFocusTo, title, ...rest }: Props) {
  // A ref rather than state: only the Escape/scrim path reads it, and that
  // runs outside the form that owns the in-flight request.
  const busy = useRef(false)

  function cancel() {
    // Not while the request is in flight — the same rule `ConfirmDialog` has.
    if (busy.current) return
    emit('confirmation_result', { mode, result_status: 'cancelled' })
    onCancel()
  }

  return (
    <Overlay open={open} onClose={cancel} label={title} restoreFocusTo={restoreFocusTo}>
      <ReauthForm mode={mode} title={title} onCancel={cancel} busy={busy} {...rest} />
    </Overlay>
  )
}

function ReauthForm({
  mode,
  title,
  body,
  confirmLabel,
  pendingLabel = 'Working…',
  destructive = false,
  onCancel,
  onReauthed,
  busy: busyRef,
}: Omit<Props, 'open' | 'restoreFocusTo'> & { busy: RefObject<boolean> }) {
  const auth = useAuth()
  const method = auth.reauthMethod()
  const [step, setStep] = useState<Step>({ kind: 'credential' })
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const recaptcha = useRef<HTMLDivElement>(null)
  /**
   * Has re-authentication already SUCCEEDED in this dialog? Once it has, a
   * failure of the gated work is retried as that work alone: the second-factor
   * resolver is spent, so asking for the code again would submit it to nothing.
   */
  const [reauthed, setReauthed] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * Focus never rests on a disabled control. Pressing the confirm disables it,
   * and a browser then blurs it to `<body>` — outside the trap. Park it on the
   * form while busy (`ConfirmDialog` does the same), and put it back on the
   * field that has to be corrected when an attempt fails.
   */
  useEffect(() => {
    if (busy) formRef.current?.focus()
  }, [busy])
  useEffect(() => {
    if (!busy && error !== null) inputRef.current?.focus()
  }, [busy, error])
  // The second-factor field arrives without an error to trigger the effect
  // above, and a keyboard or screen-reader user would otherwise be left on the
  // form with nothing saying the code box exists.
  const onCodeStep = step.kind === 'code'
  useEffect(() => {
    if (onCodeStep) inputRef.current?.focus()
  }, [onCodeStep])

  function working(on: boolean) {
    busyRef.current = on
    setBusy(on)
  }

  // Released if the dialog goes away mid-flight, so a stale `true` cannot
  // make the next one un-cancellable.
  useEffect(
    () => () => {
      busyRef.current = false
    },
    [busyRef],
  )

  const challenge = step.kind === 'code' ? step.challenge : null
  /**
   * Which second factor the code goes to. The user's pick if it is still one of
   * this challenge's factors, otherwise the first that needs no delivery — the
   * authenticator costs nothing to choose, an SMS is sent the moment its factor
   * is. With neither a pick nor an authenticator, only a SINGLE factor is chosen
   * for the user: with two phones, `factors[0]` would spend an SMS on a number
   * they have not picked, so nothing is chosen until they do.
   */
  const [picked, setPicked] = useState<string | null>(null)
  const factor =
    challenge?.factors.find((f) => f.id === picked) ??
    challenge?.factors.find((f) => !f.needsDelivery) ??
    (challenge?.factors.length === 1 ? challenge.factors[0] : undefined)
  const [sending, setSending] = useState(false)
  const pickReasonId = useId()
  const needsPick = challenge !== null && factor === undefined
  // Synchronous twin of `sending`, for the guards that read it before a render.
  const sendingRef = useRef(false)
  // The selection as it is NOW, for an async continuation (see MfaChallenge).
  const selectedNow = useRef<string | undefined>(undefined)
  selectedNow.current = factor?.id
  // Moves focus to the code box after a send, when no error is there to do it.
  const [focusCode, setFocusCode] = useState(0)
  useEffect(() => {
    if (focusCode > 0) inputRef.current?.focus()
  }, [focusCode])

  // What a live region says about a delivered code. Blanked then restored so an
  // identical repeat is still a change.
  const [note, setNote] = useState('')
  const noteTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(noteTimer.current), [])
  const announce = useCallback((sent: MfaFactor) => {
    window.clearTimeout(noteTimer.current)
    setNote('')
    noteTimer.current = window.setTimeout(
      () => setNote(`We sent a code to ${sent.hint ?? 'your phone'}.`),
      120,
    )
  }, [])

  /**
   * Bring the chosen factor's code into existence — the first, automatic send
   * and a resend alike, so both are in flight the same way.
   *
   * A result for a factor the user has since left is dropped: a late SMS failure
   * must not land above the authenticator's code box.
   */
  const send = useCallback(
    async (target: MfaFactor) => {
    const container = recaptcha.current
    if (challenge === null || container === null || sendingRef.current) return
    sendingRef.current = true
    setError(null)
    setSending(true)
    try {
      await challenge.prepare(target.id, container)
      if (selectedNow.current !== target.id) return
      setCode('')
      setFocusCode((n) => n + 1)
      announce(target)
    } catch (thrown) {
      if (selectedNow.current !== target.id) return
      setError(messageFor(thrown))
    } finally {
      sendingRef.current = false
      setSending(false)
    }
    },
    [challenge, announce],
  )

  // An SMS has to be sent before there is a code to type. Keyed on the CHOSEN
  // factor: choosing Text message is what asks for one, and choosing the
  // authenticator first must spend none. `sending` is a dependency so a factor
  // chosen while another send is in flight goes out when that one settles.
  const sentFor = useRef(new Set<string>())
  useEffect(() => {
    if (challenge === null || factor === undefined) return
    if (sentFor.current.has(factor.id) || sendingRef.current) return
    if (!factor.needsDelivery || challenge.prepared(factor.id)) {
      sentFor.current.add(factor.id)
      return
    }
    sentFor.current.add(factor.id)
    void send(factor)
  }, [challenge, factor, sending, send])

  function choose(id: string) {
    if (id === factor?.id) return
    setPicked(id)
    setCode('')
    setError(null)
    window.clearTimeout(noteTimer.current)
    setNote('')
  }

  function resend() {
    if (factor !== undefined) void send(factor)
  }

  /** Re-authentication has resolved; now, and only now, the gated work. */
  async function proceed() {
    await onReauthed()
    emit('confirmation_result', { mode, result_status: 'confirmed' })
  }

  async function attempt(run: () => Promise<void>) {
    setError(null)
    working(true)
    try {
      await run()
    } catch (thrown) {
      if (thrown instanceof MfaRequiredError) {
        sentFor.current.clear()
        setPicked(null)
        setStep({ kind: 'code', challenge: thrown.challenge })
        return
      }
      emit('confirmation_result', { mode, result_status: 'failed' })
      const lapsed = thrown instanceof AuthFailure && thrown.detail.code === REQUIRES_RECENT_LOGIN
      // The global sentence says to sign out and back in, which is wrong above a
      // password field that is about to do exactly that job.
      // Likewise `invalid-credential`: its global sentence is about an email and
      // password "matching an account", which is sign-in's question, not this one's.
      const code = thrown instanceof AuthFailure ? thrown.detail.code : null
      const wrongPassword = code === 'auth/invalid-credential' || code === 'auth/wrong-password'
      setError(
        lapsed
          ? 'That took too long. Confirm it’s you again.'
          : wrongPassword
            ? 'That password isn’t right. Try again.'
            : messageFor(thrown),
      )
      if (lapsed) {
        // The licence lapsed between the two: start the re-authentication over.
        setReauthed(false)
        sentFor.current.clear()
        setStep({ kind: 'credential' })
      }
    } finally {
      // Whatever happened, the secret was for that one attempt.
      setPassword('')
      setCode('')
      working(false)
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (reauthed) {
      // Only the gated work failed; retry it, not the re-authentication.
      void attempt(proceed)
      return
    }
    if (challenge !== null) {
      // Two phones and no pick yet: there is no factor to submit a code to.
      if (factor === undefined) return
      void attempt(async () => {
        await challenge.submit(factor.id, code)
        setReauthed(true)
        await proceed()
      })
      return
    }
    void attempt(async () => {
      await auth.reauthenticate(method === 'password' ? password : undefined)
      setReauthed(true)
      await proceed()
    })
  }

  const label =
    !reauthed && step.kind === 'credential' && method === 'google'
      ? 'Continue with Google'
      : confirmLabel

  return (
    <form className="dialog" onSubmit={submit} noValidate ref={formRef} tabIndex={-1}>
      <h2 className="dialog__title">{title}</h2>
      <p className="dialog__body">{body}</p>
      {!reauthed && step.kind === 'credential' && method === 'password' && (
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          ref={inputRef}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      )}
      {!reauthed && step.kind === 'credential' && method === 'google' && (
        <p className="dialog__body">Confirm it&apos;s you with Google to continue.</p>
      )}
      {!reauthed && challenge !== null && challenge.factors.length > 1 && (
        // Not native radios: `SegmentedControl` draws every pixel from a class a
        // colour sweep can read.
        <SegmentedControl
          label="Verification method"
          name="reauth-factor"
          tone="outline"
          disabled={busy}
          describedBy={factor === undefined ? pickReasonId : undefined}
          value={factor?.id ?? ''}
          onChange={choose}
          options={challenge.factors.map((f) => ({
            value: f.id,
            label: labelFor(f),
          }))}
        />
      )}
      {!reauthed && challenge !== null && factor === undefined && (
        <p className="dialog__body" id={pickReasonId}>
          Choose where to send your code.
        </p>
      )}
      {!reauthed && challenge !== null && challenge.factors.some((f) => f.needsDelivery) && (
        <p className="dialog__body" role="status">
          {note}
        </p>
      )}
      {!reauthed && step.kind === 'code' && factor !== undefined && (
        <Field
          label={
            factor.kind === 'phone'
              ? 'Verification code from your text message'
              : 'Verification code from your authenticator app'
          }
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      )}
      {method === null && (
        <p className="dialog__error" role="alert">
          <span className="dialog__error-mark">
            <Icon glyph={CircleX} />
          </span>
          {/* True for this case: the sentence a mapped code carries tells the
              user to sign out and back in, which does not help an account this
              screen cannot re-verify. */}
          This account&apos;s sign-in method can&apos;t be re-verified in the browser, so this
          can&apos;t be done here.
        </p>
      )}
      {error !== null && (
        // Its own live region: it arrives after the dialog opened, and
        // `Overlay` announces its contents once, on open.
        <p className="dialog__error" role="alert">
          <span className="dialog__error-mark">
            <Icon glyph={CircleX} />
          </span>
          {error}
        </p>
      )}
      <div className="dialog__actions">
        <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        {!reauthed && factor?.needsDelivery === true && (
          <Button type="button" variant="outline" size="page" disabled={busy || sending} onClick={resend}>
            {sending ? 'Sending…' : 'Send a new code'}
          </Button>
        )}
        {method !== null && (
          <Button
            type="submit"
            variant={destructive ? 'destructive' : 'primary'}
            size="page"
            disabled={busy}
            // Off for a policy reason, so it stays reachable and says why; `submit`
            // returns early with no factor, which makes the click inert.
            aria-disabled={needsPick || undefined}
            aria-describedby={needsPick ? pickReasonId : undefined}
            aria-busy={busy}
          >
            {busy ? pendingLabel : label}
          </Button>
        )}
      </div>
      {/* Owned here, for the reason `AddPhone` gives: the widget is a UI that
          needs somewhere to fail visibly. */}
      <div className="enrol__recaptcha" ref={recaptcha} />
    </form>
  )
}
